/**
 * Dine-in session slice. Lives in sessionStorage so:
 *   - The cart survives navigation between Menu / Orders / Pay Bill
 *     tabs and full page reloads (customer accidentally closing the
 *     browser tab is a common pattern).
 *   - Closing the tab DOES nuke the cart — different customer, same
 *     QR sticker shouldn't inherit a half-built order.
 *
 * NOT persisted to localStorage on purpose — see §1 of CLAUDE.md.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CartLine, CustomerInfo, DineState, TableContext } from './types';

const initialState: DineState = {
  table: null,
  lines: [],
  orderNote: '',
  customer: null,
  placedOrderIds: [],
  billInvoiceId: null,
};

const slice = createSlice({
  name: 'dine',
  initialState,
  reducers: {
    setTable(state, action: PayloadAction<TableContext | null>) {
      // If the QR token changes we treat it as a new session — drop
      // any cart belonging to the previous table so we don't carry
      // food from table 4 over to table 9.
      const next = action.payload;
      const sameSession =
        state.table &&
        next &&
        state.table.qrToken === next.qrToken &&
        state.table.tenantCode === next.tenantCode;
      if (!sameSession) {
        state.lines = [];
        state.orderNote = '';
        state.placedOrderIds = [];
        state.billInvoiceId = null;
      }
      state.table = next;
    },
    addLine(state, action: PayloadAction<Omit<CartLine, 'qty'> & { qty?: number }>) {
      const incoming = action.payload;
      const existing = state.lines.find((l) => l.menuItemId === incoming.menuItemId);
      if (existing) {
        existing.qty += incoming.qty ?? 1;
      } else {
        state.lines.push({ ...incoming, qty: incoming.qty ?? 1 });
      }
    },
    incrementLine(state, action: PayloadAction<string>) {
      const line = state.lines.find((l) => l.menuItemId === action.payload);
      if (line) line.qty += 1;
    },
    decrementLine(state, action: PayloadAction<string>) {
      const line = state.lines.find((l) => l.menuItemId === action.payload);
      if (!line) return;
      line.qty -= 1;
      if (line.qty <= 0) {
        state.lines = state.lines.filter((l) => l.menuItemId !== action.payload);
      }
    },
    setLineNote(state, action: PayloadAction<{ menuItemId: string; note: string }>) {
      const line = state.lines.find((l) => l.menuItemId === action.payload.menuItemId);
      if (line) line.note = action.payload.note;
    },
    setOrderNote(state, action: PayloadAction<string>) {
      state.orderNote = action.payload;
    },
    clearCart(state) {
      state.lines = [];
      state.orderNote = '';
    },
    setCustomer(state, action: PayloadAction<CustomerInfo | null>) {
      state.customer = action.payload;
    },
    recordPlacedOrder(state, action: PayloadAction<string>) {
      if (!state.placedOrderIds.includes(action.payload)) {
        state.placedOrderIds.push(action.payload);
      }
      // Once the order ships to the kitchen we clear the working cart
      // so the customer can build a follow-up round without seeing
      // the previous tickets.
      state.lines = [];
      state.orderNote = '';
    },
    setBillInvoiceId(state, action: PayloadAction<string | null>) {
      state.billInvoiceId = action.payload;
    },
    resetSession() {
      return initialState;
    },
  },
});

export const {
  setTable,
  addLine,
  incrementLine,
  decrementLine,
  setLineNote,
  setOrderNote,
  clearCart,
  setCustomer,
  recordPlacedOrder,
  setBillInvoiceId,
  resetSession,
} = slice.actions;

export const dineReducer = slice.reducer;
export type DineRootState = { dine: DineState };

/** Selectors */
export const selectLines = (s: DineRootState) => s.dine.lines;
export const selectLineCount = (s: DineRootState) =>
  s.dine.lines.reduce((sum, l) => sum + l.qty, 0);
export const selectSubtotal = (s: DineRootState) =>
  s.dine.lines.reduce((sum, l) => sum + l.qty * l.price, 0);
